import {
  InteractionEvent,
  VerificationType,
  type ExperienceSocialConnector,
} from '@logto/schemas';
import { useCallback, useContext, useMemo, useState } from 'react';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import {
  getSocialAuthorizationUrl,
  verifySocialVerification,
  identifyAndSubmitInteraction,
} from '@/apis/experience';
import useApi from '@/hooks/use-api';
import useErrorHandler, { type ErrorHandlers } from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';
import { generateState } from '@/utils/social-connectors';

type Credentials = {
  username: string;
  password: string;
};

const useDirectSocialSignIn = () => {
  const [loadingConnectorId, setLoadingConnectorId] = useState<string>();
  const handleError = useErrorHandler();
  const redirectTo = useGlobalRedirectTo();
  const { setVerificationId } = useContext(UserInteractionContext);

  const asyncGetAuthorizationUrl = useApi(getSocialAuthorizationUrl);
  const asyncVerifySocial = useApi(verifySocialVerification);
  const asyncSignInWithSocial = useApi(identifyAndSubmitInteraction);
  const submitInteractionErrorHandlers = useSubmitInteractionErrorHandler(InteractionEvent.SignIn);

  const errorHandlers: ErrorHandlers = useMemo(
    () => ({
      'session.invalid_credentials': async (error) => {
        await handleError(error);
      },
      ...submitInteractionErrorHandlers,
    }),
    [handleError, submitInteractionErrorHandlers]
  );

  const signIn = useCallback(
    async (connector: ExperienceSocialConnector, { username, password }: Credentials) => {
      const { id: connectorId } = connector;

      setLoadingConnectorId(connectorId);

      const redirectUri = `${window.location.origin}/callback/${connectorId}`;
      const state = generateState();

      const [authorizationError, authorizationResult] = await asyncGetAuthorizationUrl(
        connectorId,
        state,
        redirectUri
      );

      if (authorizationError || !authorizationResult) {
        if (authorizationError) {
          await handleError(authorizationError);
        }
        setLoadingConnectorId(undefined);
        return;
      }

      const [verifyError, verifyResult] = await asyncVerifySocial(connectorId, {
        verificationId: authorizationResult.verificationId,
        connectorData: {
          username,
          password,
          redirectUri,
        },
      });

      if (verifyError || !verifyResult) {
        if (verifyError) {
          await handleError(verifyError);
        }
        setLoadingConnectorId(undefined);
        return;
      }

      setVerificationId(VerificationType.Social, verifyResult.verificationId);

      const [signInError, signInResult] = await asyncSignInWithSocial({
        verificationId: verifyResult.verificationId,
      });

      if (signInError) {
        await handleError(signInError, errorHandlers);
        setLoadingConnectorId(undefined);
        return;
      }

      if (signInResult?.redirectTo) {
        await redirectTo(signInResult.redirectTo);
      }

      setLoadingConnectorId(undefined);
    },
    [
      asyncGetAuthorizationUrl,
      asyncSignInWithSocial,
      asyncVerifySocial,
      errorHandlers,
      handleError,
      redirectTo,
      setVerificationId,
    ]
  );

  return {
    loadingConnectorId,
    signIn,
  };
};

export default useDirectSocialSignIn;

